import koboLogo from "@/assets/logo-b.webp";
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { cx } from "@/lib/cx";
import { useCurrentUser } from "@/auth";
import { canSeeRoute, routes, type AppRoute } from "@/routes";
import { useFlowsBadgeCount } from "@/features/flows/hooks";
import { useWorkingCount } from "@/features/workers/hooks";
import { useOrgMembership } from "@/lib/useOrgMembership";
import { TONE_SOLID, enterProps } from "@/ui";

export type NavItemsProps = {
  /** `sidebar` is the PS1 text command rail; `bar` is the phone's five tabs. */
  orientation?: "sidebar" | "bar";
  /** Apply the one-time boot stagger without remounting on route changes. */
  enterOnBoot?: boolean;
  /** Account control below More in the desktop rail. */
  footer?: ReactNode;
  onNavigate?: () => void;
};

/**
 * Both navigation layouts come from the route registry. The rail leaves More
 * and the account control at the bottom, with the section links independently
 * scrollable when a short window cannot fit all of them. It remains one named
 * navigation region, so the screen reader meets every destination once.
 */
export function NavItems({
  orientation = "sidebar",
  enterOnBoot = false,
  footer,
  onNavigate,
}: NavItemsProps) {
  const isSidebar = orientation === "sidebar";
  const flowsCount = useFlowsBadgeCount();
  const email = useCurrentUser()?.email;
  const orgMember = useOrgMembership().isMember;
  const visibleRoutes = isSidebar
    ? routes.filter((route) => route.sidebar !== false && canSeeRoute(route, email, orgMember))
    : routes.filter((route) => route.tab !== undefined).sort((a, b) => a.tab! - b.tab!);
  const more = routes.find((route) => route.path === "more");

  const renderLink = ({ path, label, tabLabel, Icon }: AppRoute, index: number) => {
    const badge = path === "flows" ? flowsCount : 0;
    const link = (
      <NavLink
        key={path}
        to={`/${path}`}
        title={isSidebar ? label : undefined}
        onClick={onNavigate}
        {...(enterOnBoot ? enterProps("row", { index }) : {})}
        // Radix clones the tooltip trigger. A string className survives that
        // clone; React Router's function className does not.
        className={cx(
          "relative isolate flex select-none transition-none",
          isSidebar
            ? "ps1-command shrink-0 items-center focus-inset"
            : "flex-col items-center justify-center gap-1 px-1 py-1 text-caption font-normal text-subtle [&.active]:font-semibold [&.active]:text-accent-ink",
        )}
      >
        {({ isActive }) => (
          <>
            {isActive && !isSidebar ? (
              <span aria-hidden className="absolute top-1 z-0 h-9 w-16 rounded-control bg-accent-soft" />
            ) : null}
            {isSidebar ? (
              <>
                <span aria-hidden className="ps1-cursor">▶</span>
                <Icon className="ps1-command-icon z-10 size-5 shrink-0" />
                <span className="rail-label z-10 min-w-0 truncate">{label}</span>
              </>
            ) : (
              <>
                <Icon className={cx("z-10 size-6 shrink-0", isActive ? "opacity-100 text-accent-ink" : "opacity-75")} />
                <span className="z-10">{tabLabel ?? label}</span>
              </>
            )}
            {path === "workers" && orgMember ? <WorkingBadge sidebar={isSidebar} /> : null}
            {badge > 0 ? (
              <span
                aria-label={`${badge} Flows cards wait for you`}
                className={cx(
                  "absolute z-10 inline-flex min-w-4 items-center justify-center rounded-full px-1 text-caption font-semibold leading-4",
                  TONE_SOLID.accent,
                  isSidebar ? "ps1-badge" : "top-2 right-[calc(50%-1.2rem)]",
                )}
              >
                {badge > 99 ? "99+" : badge}
              </span>
            ) : null}
          </>
        )}
      </NavLink>
    );

    return link;
  };

  return (
    <nav
      aria-label="Sections"
      style={isSidebar ? undefined : { gridTemplateColumns: `repeat(${visibleRoutes.length}, minmax(0, 1fr))` }}
      className={isSidebar ? "flex min-h-0 flex-1 flex-col gap-2" : "grid h-16"}
    >
      {isSidebar ? (
        <>
          <div className="pixel-rail-sections flex min-h-0 w-full flex-1 flex-col gap-1.5 overflow-y-auto">
            <img src={koboLogo} alt="Kobo" className="kobo-rail-logo" />
            {visibleRoutes.map(renderLink)}
          </div>
          <div className="flex shrink-0 flex-col gap-1.5 px-3">
            {more ? renderLink(more, visibleRoutes.length) : null}
            {footer}
          </div>
        </>
      ) : visibleRoutes.map(renderLink)}
    </nav>
  );
}

/**
 * The machines working right now, on the Workers link: a slow-breathing brand-orange dot and the count.
 * Its own component, so only org members (who can see Workers) subscribe to the workers list.
 */
function WorkingBadge({ sidebar }: { sidebar: boolean }) {
  const working = useWorkingCount();
  if (working <= 0) return null;
  return (
    <span
      aria-label={`${working} workers working`}
      title={`กำลังทำ ${working} เครื่อง`}
      className={cx("working-badge absolute z-10", sidebar ? "ps1-badge" : "top-2 right-[calc(50%-1.2rem)]")}
    >
      <span aria-hidden className="working-badge-dot" />
      {working > 99 ? "99+" : working}
    </span>
  );
}
